import React, { useEffect, useState } from 'react'
import { useOverlay } from '@fs/zion-ui'
import { getPreference, setPreference } from '@fs/zion-preferences'
import { useNamesExperienceToggle } from '../../../../hooks/useNamesExperienceOptIn'
import AlreadyTurnedOnDialog from './AlreadyTurnedOnDialog'
import NotTurnedOnDialog from './NotTurnedOnDialog'

// Each dialog is shown at most once. Persisted server-side (across devices).
const NOT_TURNED_ON_SEEN_KEY = 'rip.fullTextAttachFlow.onboardingDialog.notTurnedOn.seen'
const ALREADY_TURNED_ON_SEEN_KEY = 'rip.fullTextAttachFlow.onboardingDialog.alreadyTurnedOn.seen'

/**
 * Shows the one-time onboarding dialog for the updated Names experience: the opt-in invite for
 * opted-out users, or the changelog for opted-in users. Each is shown once.
 *
 * @returns {JSX.Element}
 */
export default function AttachFlowOnboardingDialog() {
  const { isOptedIn, turnOn, loading, rolloutEnabled } = useNamesExperienceToggle()
  const alreadyTurnedOnOverlay = useOverlay()
  const notTurnedOnOverlay = useOverlay()
  const [checked, setChecked] = useState(false)

  // Open the changelog once, marking it seen. Shared by the initial check and the turn-on flow.
  const showAlreadyTurnedOnOnce = () =>
    getPreference(ALREADY_TURNED_ON_SEEN_KEY).then((seen) => {
      if (seen) return
      setPreference(ALREADY_TURNED_ON_SEEN_KEY, true)
      alreadyTurnedOnOverlay.open()
    })

  useEffect(() => {
    // Wait for the opt-in state to resolve; otherwise isOptedIn reads its false default and always
    // picks the invite. The loading dep re-runs this once it settles.
    if (checked || loading) return undefined
    // Master kill-switch: the updated experience is unavailable, so skip onboarding entirely.
    if (!rolloutEnabled) return undefined
    let mounted = true

    const key = isOptedIn ? ALREADY_TURNED_ON_SEEN_KEY : NOT_TURNED_ON_SEEN_KEY

    getPreference(key)
      .then((seen) => {
        if (!mounted || seen) return
        setPreference(key, true)
        if (isOptedIn) {
          alreadyTurnedOnOverlay.open()
        } else {
          notTurnedOnOverlay.open()
        }
      })
      .catch((error) => {
        console.error('[AttachFlowOnboardingDialog] Failed to check onboarding preference:', error)
      })
      .finally(() => {
        if (mounted) setChecked(true)
      })

    return () => {
      mounted = false
    }
    // isOptedIn/overlays intentionally excluded to avoid re-checking on toggle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, loading, rolloutEnabled])

  // Turning on from the invite chains into the changelog so the user sees what's new right away.
  const handleTurnOn = () => {
    turnOn()
    showAlreadyTurnedOnOnce().catch((error) => {
      console.error('[AttachFlowOnboardingDialog] Failed to show changelog after turn on:', error)
    })
  }

  return (
    <>
      <AlreadyTurnedOnDialog overlay={alreadyTurnedOnOverlay} />
      <NotTurnedOnDialog overlay={notTurnedOnOverlay} onTurnOn={handleTurnOn} />
    </>
  )
}
